/**
 * Authenticated calls against the same rivyoo-server endpoints the mobile
 * app's Profile/Privacy tabs use (`auth_service` + `user_profiles`). The
 * OAuth2 access token from `AuthContext` authenticates fine here too;
 * `oauth2_provider.contrib.rest_framework.OAuth2Authentication` is in
 * rivyoo-server's DEFAULT_AUTHENTICATION_CLASSES, and these views only
 * require IsAuthenticated (+ IsOnboarded for writes/reviews), not a
 * specific OAuth scope.
 */

import { getApiBaseUrl } from "@/lib/api/config";

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function authedFetch(accessToken, url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    let detail = "";
    try {
      const data = await res.json();
      detail = data?.error || data?.detail || "";
    } catch {
      // Non-JSON error body; fall back to the status text below.
    }
    throw new ApiError(detail || `Request failed (${res.status})`, res.status);
  }

  if (res.status === 204) return null;
  return res.json();
}

/** GET /auth/user/visibility/ -> { review_visibility, network_preference } */
export function getVisibilitySettings(accessToken) {
  return authedFetch(accessToken, `${getApiBaseUrl()}/auth/user/visibility/`);
}

/** PUT /auth/user/visibility/update/ (both fields optional) */
export function updateVisibilitySettings(accessToken, payload) {
  return authedFetch(accessToken, `${getApiBaseUrl()}/auth/user/visibility/update/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * GET /profiles/me/reviews/ -> { count, next, previous, results }.
 * Pass `nextUrl` (the absolute URL from a previous response) to paginate;
 * it's already the fully-qualified endpoint, so it's fetched as-is.
 */
export function getMyReviews(accessToken, { limit = 10, offset = 0, nextUrl } = {}) {
  const url =
    nextUrl ||
    `${getApiBaseUrl()}/profiles/me/reviews/?${new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    }).toString()}`;
  return authedFetch(accessToken, url);
}

export { ApiError };
